/**
 * ============================================================================
 * WELDING REPAIR ANALYSIS VIEW ALL MODULE
 * ============================================================================
 * 
 * PURPOSE:
 * Comprehensive view for displaying welding repair analysis data in different contexts.
 * Shows repair workflows, project statistics, and approval tracking in grid format.
 * 
 * DATA FETCHING STRATEGY:
 * 1. getApprovedPendingRepairWorkflow() - Fetches repair workflows based on user role/context
 *    - "AllUserRoleWise": Gets repairs pending with current user's role
 *    - "AllUserIdWise": Gets repairs created by current user
 * 2. getProjectReqCount() - Fetches project-wise repair statistics from repair analysis data
 * 3. getWIReqCount() - CRITICAL: Gets total joint count from WI (Welding Instructions) per project
 *    - This is the baseline for calculating repair success rates
 *    - WI contains all joints that need to be welded in a project
 *    - Each joint in WI represents a potential repair point
 * 
 * WI INTEGRATION DETAILS:
 * - WI (Welding Instructions) contains master data of all joints per project
 * - getWIReqCount(projectNo) returns total number of joints from WI for that project
 * - This total is used as denominator for success percentage calculation
 * - Formula: Success % = ((Total WI Joints - Repair Count) / Total WI Joints) * 100
 * - Higher percentage = Better welding quality (fewer repairs needed)
 * 
 * FUNCTIONALITY BY ACTION TYPE:
 * - "ViewReceivedforapproval": Shows repairs pending approval from current user
 * - "ViewRaisedforapproval": Shows repairs raised by current user
 * - "viewReqCount": Shows project-wise repair statistics with WI-based success percentages
 * 
 * KEY FEATURES:
 * - Dynamic grid columns based on action type
 * - Aging calculation (days since initiation)
 * - Clickable report numbers for navigation to detail view
 * - Global search and export functionality
 * - Real-time data filtering and aggregation
 * - WI-based success rate calculations for quality metrics
 * 
 * DATA PROCESSING:
 * - Calculates aging in days from initiation date
 * - Aggregates project data by project number
 * - Computes first-attempt success percentages using WI joint counts as baseline
 * - Cross-references repair data with WI master data for accurate metrics
 * - Filters data based on user role and context
 */
import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { getApprovedPendingRepairWorkflow, getProjectReqCount } from "../../Services/weldingRepairAnalysis-service";
import GridMaster from "../utility/grid/GridMaster";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import { handleExport1 } from "../utility/data/export/GlobalExport";
import GolbalSearch from "../utility/common/helpers/GolbalSearch";
import Export_blue from "../../icons/Export_blue.svg";
import ExportIcon from "../../icons/ExportIcon.svg";
import { useLocation, useNavigate } from "react-router";
import { getCurrentDateTime } from "../../functions/welding-functions";
import backarrow from "../../icons/Back Arrow-3.png";
import { getWIReqCount } from "../../Services/WI-services";

const HyperLink = ({ data, navigate }) => (
  <Button
    variant="link"
    onClick={() => navigate("/weldingRepair/WeldingRepairAnalysisEntry", {
      state: { data, action: "Edit" }
    })}
    size="sm"
  >
    {data.reportNumber}
  </Button>
);

const WeldingRepairAnalysisViewAll = forwardRef((props, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const childRef = useRef();

  // State variables
  const [filteredResult] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [, setShowForm] = useState(false);
  const [action, setAction] = useState(location.state?.action);
  const [reload, setReload] = useState(false);
  const [excelData1, setExcelData] = useState([]);
  const [allPendingRepairAnalysis, setAllPendingRepairAnalysis] = useState([]);
  const [allRaisedRepairAnalysis, setAllRaisedRepairAnalysis] = useState([]);
  const [columnReceivedforApproval, setColumnReceivedforApproval] = useState([]);
  const [columnRaisedforApproval, setColumnRaisedforApproval] = useState([]);
  const [ReqCount, setReqCount] = useState([]);

  // User info
  const userId = sessionStorage.getItem("UserId")?.split("@")[0];
  const userRole = sessionStorage.getItem("Role");
  const todayDate = getCurrentDateTime("-");

  // Column definitions
  const tableDefinitions = {
    received: [
      {
        headerName: "Status",
        field: "repairAnalysisStatus",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "WeldRepairRenderer",
        sortable: true,
      },
      {
        headerName: "Report Number",
        field: "reportNumber",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "HyperLink",
        minWidth: 50,
      },
      {
        headerName: "Ageing (Days)",
        field: "aging(days)",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
        sort: "desc",
      },
    ],
    raised: [
      {
        headerName: "Status",
        field: "repairAnalysisStatus",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "WeldRepairRenderer",
        sortable: true,
      },
      {
        headerName: "Report Number",
        field: "reportNumber",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "HyperLink",
        minWidth: 50,
      },
      {
        headerName: "Pending with",
        field: "approvalPendingWith",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
        minWidth: 50,
      },
      {
        headerName: "Ageing (Days)",
        field: "aging(days)",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
        sort: "desc",
      },
    ],
    project: [
      {
        headerName: "Project Number",
        field: "projectNo",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
        sortable: true,
        sort: "asc",
      },
      {
        headerName: "Repair Count",
        field: "ApprovedCount",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
        minWidth: 50,
      },
      {
        headerName: "% First Attempt Clear",
        field: "percentage",
        type: "Text",
        isCheckbox: false,
        cellRenderer: "",
      },
    ]
  };



  // Helper functions for column definitions
  const getHeaderAndFieldFunctions = (tableType) => {
    const tableDef = tableDefinitions[tableType];
    return {
      headerName: (field) => getHeader_FieldName(field, tableDef, "getHeader"),
      fieldName: (header) => getHeader_FieldName(header, tableDef, "getField")
    };
  };

  // Calculate aging days - Time since repair was initiated
  const calculateAgingDays = (item) => {
    const date1 = new Date(item.initiatedDate);
    const date2 = new Date(todayDate);
    const timeDiff = date2.getTime() - date1.getTime();
    return Math.round(timeDiff / (1000 * 60 * 60 * 24));
  };

  // Fetch data functions
  const fetchApprovedPendingRepair = async () => {
    const yeardata = new Date().getFullYear();
    const userObj = {
      userId,
      userRole,
      flag: "AllUserRoleWise",
      year: yeardata
    };

    const repairresult = await getApprovedPendingRepairWorkflow(userObj);

    if (repairresult?.length > 0) {
      const temp = repairresult
        .map(item => ({
          ...item,
          "aging(days)": calculateAgingDays(item)
        }))
        .filter(item => item.approvalPendingWith === userRole);

      setAllPendingRepairAnalysis(temp);
    } else {
      setAllPendingRepairAnalysis([]);
    }
  };

  const fetchAllRepairWorkflowByUserIdWise = async () => {
    const yeardata = new Date().getFullYear();
    const userObj = {
      userId,
      userRole,
      flag: "AllUserIdWise",
      year: yeardata
    };

    const repairresult = await getApprovedPendingRepairWorkflow(userObj);

    if (repairresult?.length > 0) {
      const raised = repairresult
        .map(item => ({
          ...item,
          "aging(days)": calculateAgingDays(item)
        }))
        .filter(item => item.createdBy === userId);

      setAllRaisedRepairAnalysis(raised);
    } else {
      setAllRaisedRepairAnalysis([]);
    }
  };

  const fetchprojectreqcount = async () => {
    const Projectcount = await getProjectReqCount();

    // Group by projectNo - Aggregate repair counts per project
    const aggregatedProjects = Projectcount.reduce((acc, project) => {
      const projectNo = project.SK.split("#")[0];

      if (!acc[projectNo]) {
        acc[projectNo] = {
          ...project,
          projectNo,
          ApprovedCount: project.ApprovedCount || 0
        };
      } else if (project.ApprovedCount) {
        acc[projectNo].ApprovedCount += project.ApprovedCount;
      }

      return acc;
    }, {});

    // CRITICAL: Cross-reference with WI data for success percentage calculation
    const finalcount = await Promise.all(
      Object.values(aggregatedProjects).map(async (project) => {
        // Get total joint count from WI (Welding Instructions) - This is the baseline
        const total = Number(await getWIReqCount(project.projectNo)) || 0;
        
        // Calculate success percentage: (Total WI Joints - Repairs) / Total WI Joints * 100
        // Higher percentage = Better quality (fewer repairs needed)
        const percentage = total > 0
          ? Math.round(((total - project.ApprovedCount) / total) * 100)
          : 100;

        return {
          ...project,
          count: String(total), // Total joints from WI
          percentage: `${percentage}%` // Success rate based on WI baseline
        };
      })
    );

    setReqCount(finalcount);
    fetchColumnDefns();
  };

  // Initialize column definitions
  const fetchColumnDefns = () => {
    setColumnDefs(GlobalColumnDefinations(tableDefinitions.project));
  };

  const fetchColumnReceivedforApproval = () => {
    const result = GlobalColumnDefinations(tableDefinitions.received);
    result[1].cellRenderer = (params) => <HyperLink data={params.data} navigate={navigate} />;
    setColumnReceivedforApproval(result);
  };

  const fetchColumnRaisedforApproval = () => {
    const result = GlobalColumnDefinations(tableDefinitions.raised);
    result[1].cellRenderer = (params) => <HyperLink data={params.data} navigate={navigate} />;
    setColumnRaisedforApproval(result);
  };

  // Event handlers
  const handleGlobalSearch = (text) => {
    childRef.current.setSearch(text);
  };

  const handleGlobalExport = () => {
    const filename = "WeldingRepairAnalysis";
    setExcelData(filteredResult);
    handleExport1(excelData1, filename, tableDefinitions.received);
  };

  // Effects
  useEffect(() => {
    fetchApprovedPendingRepair();
    fetchColumnReceivedforApproval();
    fetchAllRepairWorkflowByUserIdWise();
    fetchColumnRaisedforApproval();
  }, [reload]);

  useEffect(() => {
    fetchprojectreqcount();
  }, []);

  // Imperative handle
  useImperativeHandle(ref, () => ({
    handleAdd: () => {
      setAction("Add");
      setShowForm(true);
      setReload(true);
    },
  }));

  // Get header/field functions based on action
  const { headerName, fieldName } = getHeaderAndFieldFunctions("project");
  const { headerName: headerReceivedforApproval, fieldName: fieldReceivedforApproval } =
    getHeaderAndFieldFunctions("received");
  const { headerName: headerRaisedforApproval, fieldName: fieldRaisedforApproval } =
    getHeaderAndFieldFunctions("raised");

  // Determine grid props based on action
  const getRowDetails = () => {
    if (action === "ViewReceivedforapproval") return allPendingRepairAnalysis;
    if (action === "viewReqCount") return ReqCount;
    return allRaisedRepairAnalysis;
  };

  const getColDetails = () => {
    if (action === "ViewReceivedforapproval") return columnReceivedforApproval;
    if (action === "viewReqCount") return columnDefs;
    return columnRaisedforApproval;
  };

  const getFieldNames = () => {
    if (action === "ViewReceivedforapproval") return fieldReceivedforApproval;
    if (action === "viewReqCount") return fieldName;
    return fieldRaisedforApproval;
  };

  const getHeaderNames = () => {
    if (action === "ViewReceivedforapproval") return headerReceivedforApproval;
    if (action === "viewReqCount") return headerName;
    return headerRaisedforApproval;
  };

  const getTitle = () => {
    if (action === "ViewReceivedforapproval") return "List of workflow received for approval";
    if (action === "viewReqCount") return "Project Wise Repair";
    return "List of workflow raised by logged in user for approval";
  };

  return (
    <Container fluid className="main-container">
      <Row className="RowHeight">
        <Col md={4} sm={12} xs={12} className="ListTitle">
          <button
            className="Repairviewallbackbutton"
            onClick={() => navigate("/weldingRepair/WeldingRepairAnalysis", {
              state: { data: "", action: "" }
            })}
            size="sm"
          >
            <img src={backarrow} alt="Back" />
          </button>
          {getTitle()}
        </Col>
        <Col>
          <div className="global-search-container">
            <GolbalSearch
              searchplaceholdertext="Search for Report Number, Project Number"
              filterFunc={handleGlobalSearch}
            />
            <img
              className="allexport"
              src={ExportIcon}
              onMouseOver={(e) => (e.currentTarget.src = Export_blue)}
              onMouseOut={(e) => (e.currentTarget.src = ExportIcon)}
              alt="ExportIcon"
              onClick={handleGlobalExport}
            />
          </div>
        </Col>
      </Row>

      <GridMaster
        rowDetails={getRowDetails()}
        colDetails={getColDetails()}
        fieldNames={getFieldNames()}
        headerNames={getHeaderNames()}
        setExcelData={setExcelData}
        ref={childRef}
        paginationPageSize={5}
        pagination={true}
      />
    </Container>
  );
});

export default WeldingRepairAnalysisViewAll;
